import React, { FC, Suspense } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { Loader } from 'src/redesign/components/Loader';
import { EVENTS_ROOT, ROUTES } from 'src/redesign/shared/routes';
import { SiteWrapper } from 'src/app';
import './Router.module.scss';
import { AnimatePresence, motion } from 'framer-motion';

const EventPageLoaded = React.lazy(() =>
  import('src/redesign/pages/EventsPage').then((module) => ({ default: module.EventsPage })),
);

const SiteSettingsPageLoaded = React.lazy(() =>
  import('src/redesign/pages/SiteSettingsPage').then((module) => ({
    default: module.SiteSettingsPage,
  })),
);

const EngageAndPromotionsPageLoaded = React.lazy(() =>
  import('src/redesign/pages/EngageAndPromotionPage').then((module) => ({
    default: module.EngageAndPromotionPage,
  })),
);

const TeamInvitePageLoaded = React.lazy(() =>
  import('src/redesign/pages/TeamInvitePage').then((module) => ({
    default: module.TeamInvitePage,
  })),
);

const IntegrationsPageLoaded = React.lazy(() =>
  import('src/redesign/pages/IntegrationsPage').then((module) => ({
    default: module.IntegrationsPage,
  })),
);

const CreateEventPageLoaded = React.lazy(() =>
  import('src/redesign/pages/CreateEventPage').then((module) => ({
    default: module.CreateEvent,
  })),
);
const PricingPageLoaded = React.lazy(() =>
  import('src/modules/sites/pricing').then((module) => ({
    default: module.Pricing,
  })),
);
const AttendeeListPageLoaded = React.lazy(() =>
  import('src/modules/attendees/attendee-list').then((module) => ({
    default: module.AttendeeList,
  })),
);
const AttendeeViewPageLoaded = React.lazy(() =>
  import('src/modules/attendees/attendee-view').then((module) => ({
    default: module.AttendeeView,
  })),
);
const OrderViewPageLoaded = React.lazy(() =>
  import('src/modules/attendees/order-view').then((module) => ({
    default: module.OrderView,
  })),
);
const AttendeeCheckinPageLoaded = React.lazy(() =>
  import('src/modules/attendees/attendee-checkin').then((module) => ({
    default: module.AttendeeCheckin,
  })),
);
const AttendeePublicViewPageLoaded = React.lazy(() =>
  import('src/modules/attendees/attendee-public-view').then((module) => ({
    default: module.AttendeePublicView,
  })),
);
const EditEventPageLoaded = React.lazy(() =>
  import('src/redesign/pages/CreateEventPage').then((module) => ({
    default: module.EditEvent,
  })),
);
const RequestEditEventPageLoaded = React.lazy(() =>
  import('src/redesign/pages/CreateEventPage').then((module) => ({
    default: module.RequestEditEvent,
  })),
);
const RequestCreateEventPageLoaded = React.lazy(() =>
  import('src/redesign/pages/CreateEventPage').then((module) => ({
    default: module.CreateEventPublic,
  })),
);
const CreateEventWithAIPageLoaded = React.lazy(() =>
  import('src/modules/events/create-ai-event').then((module) => ({
    default: module.CreateEventWithAI,
  })),
);
const CopyCreateEventPageLoaded = React.lazy(() =>
  import('src/modules/events/create-event').then((module) => ({
    default: module.CopyCreateEvent,
  })),
);
const EventAttendeeListPageLoaded = React.lazy(() =>
  import('src/redesign/pages/AttendeesPage').then((module) => ({
    default: module.EventAttendeeList,
  })),
);
const PayoutsPageLoaded = React.lazy(() =>
  import('src/redesign/pages/PayoutsPage').then((module) => ({
    default: module.PayoutsPage,
  })),
);
const HostPayoutsPageLoaded = React.lazy(() =>
  import('src/redesign/pages/HostPayoutsPage').then((module) => ({
    default: module.HostPayoutsPage,
  })),
);
const CampaignManagePageLoaded = React.lazy(() =>
  import('src/modules/campaigns/create-campaign').then((module) => ({
    default: module.CampaignManage,
  })),
);

const AttendeeTransactionsPageLoaded = React.lazy(() =>
  import('src/modules/transactions').then((module) => ({
    default: module.AttendeeTransactions,
  })),
);

const PromotionManagePageLoaded = React.lazy(() =>
  import('src/modules/campaigns/create-promotion').then((module) => ({
    default: module.PromotionManage,
  })),
);

const CampaignLogPageLoaded = React.lazy(() =>
  import('src/modules/campaigns/campaign-log').then((module) => ({
    default: module.CampaignLog,
  })),
);

const NotificationManagePageLoaded = React.lazy(() =>
  import('src/modules/campaigns/create-notification').then((module) => ({
    default: module.NotificationManage,
  })),
);

const WidgetSettingsPageLoaded = React.lazy(() =>
  import('src/redesign/pages/WidgetDesign').then((module) => ({
    default: module.WidgetDesign,
  })),
);

const DesignPageLoaded = React.lazy(() =>
  import('src/redesign/pages/Design').then((module) => ({
    default: module.Design,
  })),
);

const CampaignStatsPageLoaded = React.lazy(() =>
  import('src/modules/campaigns/campaign-stats').then((module) => ({
    default: module.CampaignStats,
  })),
);

const AdminDashboardPageLoaded = React.lazy(() =>
  import('src/redesign/pages/AdminDashboardPage').then((module) => ({
    default: module.AdminDashboardPage,
  })),
);

const AnalyticsPageLoaded = React.lazy(() =>
  import('src/redesign/features/Analytics').then((module) => ({
    default: module.Analytics,
  })),
);

const SeatingChartsPageLoaded = React.lazy(() =>
  import('src/redesign/pages/SeatingChartsPage').then((module) => ({
    default: module.SeatingChartsPage,
  })),
);

export const EventRouter: FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence exitBeforeEnter>
      <Switch location={location} key={location}>
        <Route
          path={EVENTS_ROOT}
          exact
          component={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                key={EVENTS_ROOT}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <EventPageLoaded />
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          path={ROUTES.siteSettings}
          exact
          component={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <SiteSettingsPageLoaded includeDesign={false} />
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          path={ROUTES.promotional}
          exact
          component={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <EngageAndPromotionsPageLoaded />
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          path={ROUTES.team}
          exact
          component={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                key={ROUTES.team}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <TeamInvitePageLoaded />
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          path={ROUTES.integrations}
          exact
          component={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                key={ROUTES.integrations}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <IntegrationsPageLoaded />
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          path={ROUTES.analytics}
          exact
          component={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                key={ROUTES.analytics}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <AnalyticsPageLoaded />
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          path={ROUTES.seatingCharts}
          exact
          component={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                key={ROUTES.seatingCharts}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <SeatingChartsPageLoaded />
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          exact
          path={ROUTES.design}
          render={() => <Redirect to={`${ROUTES.design}/brand`} />}
        />
        <Route
          exact
          path={`${ROUTES.design}/:tab/:type?`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <SiteWrapper {...props}>
                  <DesignPageLoaded />
                </SiteWrapper>
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          exact
          path={ROUTES.designWidget}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <SiteWrapper {...props}>
                  <WidgetSettingsPageLoaded />
                </SiteWrapper>
              </motion.div>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/manage`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <CreateEventPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/manage-request`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <RequestCreateEventPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/billing`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props} noAuth>
                <PricingPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/attendees/manage`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <AttendeeListPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/attendees/manage/:id?`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <AttendeeViewPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/orders/manage/:id`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <OrderViewPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/attendees/checkin`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <AttendeeCheckinPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/attendees/view/:id?`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <AttendeePublicViewPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/manage/:id/request`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <RequestEditEventPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/ai/manage`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <CreateEventWithAIPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/copy/:id`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <CopyCreateEventPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/manage/:id/attendees/card/:cardId?`}
          render={(props) => {
            const { id } = props.match.params;
            return (
              <Suspense fallback={<Loader />}>
                <SiteWrapper {...props}>
                  <EventAttendeeListPageLoaded key={id || 'attendees'} />
                </SiteWrapper>
              </Suspense>
            );
          }}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/manage/:id/attendees/:attendeeId?`}
          render={(props) => {
            const { id } = props.match.params;
            return (
              <Suspense fallback={<Loader />}>
                <SiteWrapper {...props}>
                  <EventAttendeeListPageLoaded key={id || 'attendees'} />
                </SiteWrapper>
              </Suspense>
            );
          }}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/manage/:id/attendees`}
          render={(props) => {
            const { id } = props.match.params;
            return (
              <Suspense fallback={<Loader />}>
                <SiteWrapper {...props}>
                  <EventAttendeeListPageLoaded key={id || 'attendees'} />
                </SiteWrapper>
              </Suspense>
            );
          }}
        ></Route>
        <Route
          exact
          path={`${EVENTS_ROOT}/manage/:id/:step?`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <EditEventPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/payouts`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <PayoutsPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/host-payouts`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <HostPayoutsPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/transactions`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <AttendeeTransactionsPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/campaigns/manage/:id?`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <CampaignManagePageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/promotions/manage/:id?`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <PromotionManagePageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/campaigns/manage/:id/logs`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <CampaignLogPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/campaigns/manage/:id/stats`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <CampaignStatsPageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={`${EVENTS_ROOT}/campaigns/manage/:campaign_id/notification/:id?`}
          render={(props) => (
            <Suspense fallback={<Loader />}>
              <SiteWrapper {...props}>
                <NotificationManagePageLoaded />
              </SiteWrapper>
            </Suspense>
          )}
        />
        <Route
          exact
          path={ROUTES.adminDashboard}
          render={() => (
            <Suspense fallback={<Loader />}>
              <motion.div
                key={ROUTES.adminDashboard}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                }}
                transition={{ duration: 0.1 }}
              >
                <AdminDashboardPageLoaded />
              </motion.div>
            </Suspense>
          )}
        />
      </Switch>
    </AnimatePresence>
  );
};
